import React, { useState, useMemo, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MdSearch, MdExpandMore, MdFilterList, MdClose } from 'react-icons/md';
import type { MenuItem } from '../menuItems/types';

interface MenuSelectorProps {
  items: MenuItem[];
  selectedItems: string[];
  onSelectionChange: (selectedIds: string[]) => void;
  loading?: boolean;
  disabled?: boolean;
}

const MenuSelector: React.FC<MenuSelectorProps> = ({ 
  items, 
  selectedItems, 
  onSelectionChange,
  loading,
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Get unique categories
  const categories = useMemo(() => {
    const cats = new Set<string>();
    items.forEach(item => {
      if (item.categoryName) cats.add(item.categoryName);
    });
    return Array.from(cats).sort();
  }, [items]);

  // Filter items based on search and category
  const filteredItems = useMemo(() => {
    return items.filter(item => {
      const itemName = item.name ?? '';
      const itemCategory = item.categoryName ?? '';
      const query = searchQuery.toLowerCase();
      const matchesSearch = itemName.toLowerCase().includes(query) || itemCategory.toLowerCase().includes(query);
      const matchesCategory = selectedCategories.length === 0 || selectedCategories.includes(item.categoryName);
      return matchesSearch && matchesCategory && item.inventoryStatus;
    });
  }, [items, searchQuery, selectedCategories]);

  const toggleItem = (itemId: string) => {
    if (selectedItems.includes(itemId)) {
      onSelectionChange(selectedItems.filter(id => id !== itemId));
    } else {
      onSelectionChange([...selectedItems, itemId]);
    }
  };

  const toggleCategory = (category: string) => {
    if (selectedCategories.includes(category)) {
      setSelectedCategories(selectedCategories.filter(c => c !== category));
    } else {
      setSelectedCategories([...selectedCategories, category]);
    }
  };

  // Capitalize only the first letter of the name, keep rest as-is (lowercase)
  const formatItemName = (name: string) => {
    if (!name) return name;
    return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
  };

  const removeItem = (itemId: string) => {
    onSelectionChange(selectedItems.filter(id => id !== itemId));
  };

  const selectedItemObjects = selectedItems
    .map((itemId) => items.find((i) => i.pk_id === itemId))
    .filter((item): item is MenuItem => Boolean(item));

  return (
    <div ref={dropdownRef} className={`relative ${disabled ? 'opacity-50 pointer-events-none' : ''}`}>
      {/* Main Input - shows selected items as chips directly inside the field */}
      <div
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className={`w-full min-h-[48px] px-0 py-2 bg-transparent border-0 border-b border-gray-700 rounded-none flex items-center justify-between gap-2 transition-colors ${disabled ? 'cursor-not-allowed' : 'cursor-pointer hover:border-brand-primary'}`}
      >
        {selectedItemObjects.length > 0 ? (
          <div className="flex flex-wrap items-center gap-2 flex-1">
            {selectedItemObjects.map((item) => (
              <motion.div
                key={item.pk_id}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                className="flex items-center gap-1 px-2 py-1 rounded-full text-[#fff] font-medium text-[10px]"
                style={{ backgroundColor: '#ee442380' }}
              >
                <span>{formatItemName(item.name)}</span>
                {!disabled && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeItem(item.pk_id!);
                    }}
                    className="hover:bg-[#EE4423]/30 cursor-pointer rounded-full p-0.5 transition-colors"
                  >
                    <MdClose className="text-sm cursor-pointer" />
                  </button>
                )}
              </motion.div>
            ))}
          </div>
        ) : (
          <span className="text-gray-400 flex-1">Select menu items...</span>
        )}
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.2 }}
          className="flex-shrink-0"
        >
          <MdExpandMore className="text-white text-xl" />
        </motion.div>
      </div>


      {/* Dropdown Panel */}
      <AnimatePresence>
        {isOpen && !disabled && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="absolute top-full left-0 right-0 mt-2 bg-surface-dark border border-gray-700 rounded-xl overflow-hidden z-[60] max-h-96 flex flex-col"
          >
            {/* Search and Filter Bar */}
            <div className="p-3 border-b border-gray-700 space-y-2">
              <div className="flex items-center gap-2">
                <div className="flex-1 relative">
                  <MdSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search menu items..."
                    className="w-full pl-10 pr-4 py-2 bg-[#2a2a2a] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-brand-primary transition-colors"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => setShowFilters(!showFilters)}
                  className={`p-2 rounded-lg transition-colors cursor-pointer ${
                    showFilters ? 'bg-brand-primary text-white' : 'bg-[#2a2a2a] text-gray-400 hover:text-white'
                  }`}
                >
                  <MdFilterList className="text-xl" />
                </button>
              </div>

              {/* Category Filters */}
              <AnimatePresence>
                {showFilters && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="pt-2">
                      <p className="text-white text-xs font-medium mb-2">Filter by Category</p>
                      <div className="flex flex-wrap gap-2">
                        {categories.map((category) => (
                          <label
                            key={category}
                            className="flex items-center gap-2 px-3 py-1 bg-[#2a2a2a] rounded-lg cursor-pointer hover:bg-gray-700 transition-colors"
                          >
                            <input
                              type="checkbox"
                              checked={selectedCategories.includes(category)}
                              onChange={() => toggleCategory(category)}
                              className="w-4 h-4 rounded border-gray-600 text-brand-primary focus:ring-[#EE4423]"
                            />
                            <span className="text-white text-xs">{category}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Items List */}
            <div className="overflow-y-auto flex-1">
              {loading ? (
                <div className="p-4 text-center text-gray-400">Loading menu items...</div>
              ) : filteredItems.length === 0 ? (
                <div className="p-4 text-center text-gray-400">No items found</div>
              ) : (
                filteredItems.map((item) => {
                  if (!item.pk_id) return null;
                  return (
                    <label
                      key={item.pk_id}
                      className="flex items-center gap-3 px-4 py-3 hover:bg-gray-700/50 cursor-pointer transition-colors border-b border-gray-800 last:border-b-0"
                    >
                      <input
                        type="checkbox"
                        checked={selectedItems.includes(item.pk_id)}
                        onChange={() => toggleItem(item.pk_id!)}
                        className="w-5 h-5 rounded border-gray-600 text-brand-primary focus:ring-[#EE4423]"
                      />
                      <div className="flex-1">
                        <p className="text-white text-sm font-medium">{formatItemName(item.name)}</p>
                      </div>
                    </label>
                  );
                })
              )}
            </div>

            {/* Selected Count Footer */}
            {selectedItems.length > 0 && (
              <div className="px-4 py-2 bg-[#2a2a2a] border-t border-gray-700 flex items-center justify-between">
                <span className="text-white text-sm">{selectedItems.length} item(s) selected</span>
                <button
                  type="button"
                  onClick={() => {
                    onSelectionChange([]);
                    setIsOpen(false);
                  }}
                  className="text-xs text-gray-400 hover:text-white cursor-pointer transition-colors"
                >
                  Clear all
                </button>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MenuSelector;
